import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LENSES, LensKey } from '../lenses/lens';
import { LensGraphComponent } from '../lenses/lens-graph.component';
import { LensGraphNode } from '../lenses/lens-node-style';
import { ContextGraph } from './context-graph';

export interface ContextGraphDialogData {
    name?: string;                                  // what it is about, to title it while it's worked out
    graph: Promise<ContextGraph | undefined>;
    openLens?: (lens: LensKey, focus?: string) => void;   // to drill into the view that explains it
    openNode?: (key: string, modelKey: string) => void;   // to open a node in its model, to correct it
}

// The context of something an analysis view points at, as a small graph in a dialog: what it is about highlighted, the
// nodes around it, and a way into the view that shows it in full
@Component({
    selector: 'app-context-graph-dialog',
    standalone: true,
    imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, LensGraphComponent],
    template: `
        <div class="context" data-testid="context-graph">
            @if (loading) {
                <div class="status" role="status"><mat-spinner diameter="24"></mat-spinner><span>Working out the context{{ data.name ? ' of ' + data.name : '' }}…</span></div>
            } @else if (!graph) {
                <div class="status">
                    <mat-icon>hub</mat-icon>
                    <span>{{ data.name || 'This' }} isn't in the models looked at, so there's no graph to show.</span>
                </div>
            } @else {
                <div class="graph">
                    <app-lens-graph [nodes]="graph.nodes" [edges]="graph.edges" [title]="graph.title" [subtitle]="graph.subtitle" (nodeSelected)="select($event)"></app-lens-graph>
                </div>
                <p class="legend">
                    <span><i class="swatch focus"></i>What it's about</span>
                    @if (hasPerformers) { <span><i class="line dashed"></i>Who does it, or with what</span> }
                    @if (hasDetour) { <span><i class="line detour"></i>Exception work</span> }
                    @if (graph.truncated) { <span class="note">{{ graph.truncated }} more left out to keep it readable</span> }
                </p>
            }
            <div class="actions">
                @if (graph && data.openNode) {
                    @if (selected?.modelKey) {
                        <button mat-stroked-button color="accent" type="button" (click)="editNode()" data-testid="context-edit-node"><mat-icon>edit</mat-icon>Edit {{ selected!.label }} in its model</button>
                    } @else {
                        <span class="hint">Select a step, output, role or system to edit it in its model</span>
                    }
                }
                @if (graph && data.openLens) {
                    <button mat-flat-button color="accent" type="button" (click)="drillIn()"><mat-icon>open_in_new</mat-icon>Open in the {{ lensLabel }} view</button>
                }
                <button mat-button type="button" mat-dialog-close>Close</button>
            </div>
        </div>
    `,
    styles: [`
        .context { display: flex; flex-direction: column; box-sizing: border-box; height: min(80vh, 720px); padding: 14px 16px 12px; overflow: hidden; }
        .graph { position: relative; flex: 1 1 auto; min-height: 0; border: 1px solid var(--line, #e7e9f0); border-radius: 10px; overflow: hidden; }
        .status { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: var(--slate, #5b6172); font-size: 14px; text-align: center; }
        .status mat-icon { width: 40px; height: 40px; font-size: 40px; color: #c5cad6; }
        .legend { display: flex; flex-wrap: wrap; gap: 6px 18px; margin: 10px 2px 0; font-size: 12px; color: var(--slate, #5b6172); }
        .legend span { display: inline-flex; align-items: center; gap: 6px; }
        .legend .note { font-style: italic; }
        .swatch { width: 12px; height: 12px; border-radius: 3px; border: 1px solid #e0c98a; }
        .swatch.focus { background: #fff4d6; }
        .line { width: 22px; height: 0; border-top: 2px solid #64748B; }
        .line.dashed { border-top: 2px dashed #94a3b8; }
        .line.detour { border-top-color: #e34948; }
        .actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px; padding-top: 12px; }
        .hint { margin-right: auto; font-size: 12px; color: var(--slate, #5b6172); }
    `],
})
export class ContextGraphDialogComponent implements OnInit {
    loading = true;
    graph?: ContextGraph;
    selected?: LensGraphNode;      // the node chosen in the graph, to edit in its model

    constructor(@Inject(MAT_DIALOG_DATA) public data: ContextGraphDialogData, private dialogRef: MatDialogRef<ContextGraphDialogComponent>) {}

    async ngOnInit() {
        try {
            this.graph = await this.data.graph;
            // what it's about, when that is one node, until the user picks another
            if (this.graph?.focus.length === 1) this.select(this.graph.focus[0]);
        } catch (err) {
            console.error('Unable to work out the context graph', err);
            this.graph = undefined;
        } finally {
            this.loading = false;
        }
    }

    get hasPerformers() {
        return !!this.graph?.edges.some(e => e.dashed);
    }

    get hasDetour() {
        return !!this.graph?.edges.some(e => e.colour === '#e34948');
    }

    get lensLabel() {
        return (LENSES.find(l => l.key === this.graph?.lens)?.label || 'analysis').toLowerCase();
    }

    select(key: string | undefined) {
        this.selected = key ? this.graph?.nodes.find(n => n.key === key) : undefined;
    }

    editNode() {
        const node = this.selected;
        if (!node?.modelKey) return;
        this.dialogRef.close();
        this.data.openNode?.(node.key, node.modelKey);
    }

    drillIn() {
        if (!this.graph) return;
        this.dialogRef.close();
        this.data.openLens?.(this.graph.lens, this.graph.lensFocus);
    }
}
